import React, { useCallback, useEffect, useState } from "react";
import IAreaRiservata from "pages/AreaRiservata/IAreaRiservata";

import "./AreaRiservata.css";
import { Alert, Skeleton, Tabs } from "antd";
import useWindowDimensions from "utils/WindowSize";

import FamiliariUtente from "pages/AreaRiservata/FamiliariUtente/FamiliariUtente";
import PrenotazioniUtente from "pages/AreaRiservata/PrenotazioniUtente/PrenotazioniUtente";
import { AssistitoRepository, AuthRepository, PrestazioneRepository } from "core/data";
import i18n from "utils/i18n";
import { IFamiliare, IPrenotazione } from "core/interfaces/data/IAssistitoRequest";

import DatiUtente from "pages/AreaRiservata/DatiUtente/DatiUtente";
import PreventiviInsoluti from "pages/AreaRiservata/PrenotazioniInsolute/PrenotazioniInsolute";
import { setLogin } from "core/redux/features/session/sessionSlice";
import { IFattureData, IPreventivoData } from "core/interfaces/data/IPagamentoRequest";
import RiepilogoRepository from "core/data/RiepilogoRepository";
import { performAsyncFunction } from "utils/AsyncFunctionUtility";

import { personalAreaStep } from "types/StepEnums";
import { Enhanced } from "utils/ReactUtils";
import FattureUtente from "./FattureUtente/FattureUtente";
import { INumericArrayRequest } from "core/interfaces/data/Base";
import { IDescrizioneAttivita } from "core/interfaces/data/IPrestazioneRequest";

// import FAQ from "ReservedArea/FAQ";

const AreaRiservata: React.FC<IAreaRiservata> = ({ sessionInfo, dispatch, t, handleError }) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [selectedTab, setSelectedTab] = useState<string>(sessionInfo.step.toString());

	const [familiari, setFamiliari] = useState<IFamiliare[] | undefined>(undefined);
	const [prenotazioni, setPrenotazioni] = useState<IPrenotazione[] | undefined>(undefined);
	const [descrizioni, setDescrizioni] = useState<IDescrizioneAttivita[] | undefined>(undefined);
	const [preventivi, setPreventivi] = useState<IPreventivoData[] | undefined>(undefined);
	const [ordiniFatture, setOrdiniFatture] = useState<IFattureData[] | undefined>(undefined);

	const [isAuthError, setIsAuthError] = useState<boolean>(false);
	const [isFamError, setIsFamError] = useState<boolean>(false);
	const [isPrenError, setIsPrenError] = useState<boolean>(false);

	const { width } = useWindowDimensions();

	useEffect(()=>{
		if(sessionInfo.step==0) setSelectedTab(personalAreaStep.datiUtente)
	},[])
	const checkSession = useCallback(async () => {
		try {
			if (sessionInfo.isLogged) return sessionInfo.isLogged;

			const res = await AuthRepository.SessionCheckAuth();
			
			dispatch(setLogin([res.IsLogged, res.nomeUtente]));
			return res.IsLogged;
		} catch (err: any) {
			setIsAuthError(true);
		}
	}, [dispatch, sessionInfo.isLogged]);

	const getFamiliari = useCallback(async () => {
		try {
			const familiari = await AssistitoRepository.GetFamiliari({
				IdSessione: sessionInfo.sessione?.idSessione,
				culture: i18n.language,
			});
			return familiari;
		} catch (err: any) {
			setIsFamError(true);
		}
	}, [sessionInfo.sessione?.idSessione]);

	const getPrenotazioni = useCallback(async () => {
		try {
			const prenotazioni = await AssistitoRepository.GetPrenotazioniUtente(  i18n.language );
			return prenotazioni;
		} catch (err: any) {
			setIsPrenError(true);
		}
	}, []);

	const getDescrizioni = useCallback(async (prn: any) => {
		let igus = [];
		if (prn !== undefined) {
			for (let p of prn) {
				if (igus.indexOf(p.IguAttivita) === -1) igus.push(p.IguAttivita);
			}
		}
		if (igus.length > 0) {
			try {
				const request: INumericArrayRequest = {
					culture: i18n.language,
					Values: igus,
				};
				const dss = await PrestazioneRepository.getDescrizionePrestazioni(request);
				return dss ?? [];
			} catch (err: any) {
				return [];
			}
		}
	}, []);

	const getPreventiviSospesi = useCallback(async () => {
		try {
			const preventivi = await RiepilogoRepository.GetPreventiviInsoluti({
				IdSessione: sessionInfo.sessione?.idSessione,
				culture: i18n.language,
			});
			return preventivi;
		} catch (err: any) {
			setIsPrenError(true);
		}
	}, [sessionInfo.sessione?.idSessione]);

	const getOrdiniFatture = useCallback(async () => {
		try {
			const fatture = await RiepilogoRepository.GetOrdiniFatture({
				IdSessione: sessionInfo.sessione?.idSessione,
				culture: i18n.language,
			});
			return fatture;
		} catch (err: any) {
			setIsPrenError(true);
		}
	}, [sessionInfo.sessione?.idSessione]);

	useEffect(() => {
		performAsyncFunction(async () => {
			setLoading(true);
			const sessionActive = await checkSession();
			if (sessionActive) {
				let fam = await getFamiliari();
				setFamiliari(fam);

				let prn = await getPrenotazioni();
				setPrenotazioni(prn);

				let descrs = await getDescrizioni(prn);
				setDescrizioni(descrs as any);

				let prev = await getPreventiviSospesi();
				setPreventivi(prev);

				let fatt = await getOrdiniFatture();
				setOrdiniFatture(fatt);

				setLoading(false);
			}
		}, handleError);
	}, [checkSession, getDescrizioni, getFamiliari, getOrdiniFatture, getPrenotazioni, getPreventiviSospesi, handleError]);

	const onTabChanged = (activeKey: string, e: React.KeyboardEvent | React.MouseEvent) => {
		setSelectedTab(activeKey);
	};

	return (
		<>
			<div id="reservedArea">
				<Tabs
					defaultActiveKey={personalAreaStep.datiUtente}
					tabPosition={width > 1280 ? "left" : "top"}
					className={"reservedAreaTabsManager"}
					activeKey={selectedTab}
					onTabClick={onTabChanged}
					items={[
						{
							label: t("reservedArea.datiUtente.sectionTitle"),
							key: personalAreaStep.datiUtente,
							children: (
								<>
									{loading ? (
										<Skeleton active />
									) : !isAuthError ? (
										<DatiUtente utente={familiari?.filter((x) => x.UtenteIsRichiedente)[0]}></DatiUtente>
									) : (
										<Alert message={t("errorMessages.base")} type="error" showIcon banner />
									)}
								</>
							),
						},
						{
							label: t("reservedArea.falimiariUtente.sectionTitle"),
							key: personalAreaStep.familiari,
							children: (
								<>
									{loading ? (
										<Skeleton active />
									) : !isFamError && !isAuthError ? (
										<FamiliariUtente familiari={familiari?.filter((x) => !x.UtenteIsRichiedente)}></FamiliariUtente>
									) : (
										<Alert message={t("errorMessages.base")} type="error" showIcon banner />
									)}
								</>
							),
						},
						{
							label: t("reservedArea.prenotazioniUtente.sectionTitle"),
							key: personalAreaStep.prenotazioni,
							children: (
								<>
									{loading ? (
										<Skeleton active />
									) : !isPrenError && !isAuthError ? (
										<PrenotazioniUtente prenotazioni={prenotazioni} descrizioni={descrizioni}></PrenotazioniUtente>
									) : (
										<Alert message={t("errorMessages.base")} type="error" showIcon banner />
									)}
								</>
							),
						},
						{
							label: t("reservedArea.OrdiniFatture.sectionTitle"),
							key: personalAreaStep.fatture,
							children: (
								<>
									{loading ? (
										<Skeleton active />
									) : !isPrenError && !isAuthError ? (
										<FattureUtente fatture={ordiniFatture} />
									) : (
										<Alert message={t("errorMessages.base")} type="error" showIcon banner />
									)}
								</>
							),
						},
						{
							label: t("reservedArea.prenotazioniInsoluteUtente.sectionTitle"),
							key: personalAreaStep.sospesi,
							children: (
								<>
									{" "}
									{loading ? (
										<Skeleton active />
									) : !isPrenError && !isAuthError ? (
										<PreventiviInsoluti preventivi={preventivi} />
									) : (
										<Alert message={t("errorMessages.base")} type="error" showIcon banner />
									)}
								</>
							),
						},
					]}
				/>
			</div>
		</>
	);
};

export default Enhanced(AreaRiservata, { componentNamespace: "AreaRiservata" });
